import instance from '../utils/axios';

export interface IJobWorkType {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  output_type?: 'CREATES_INVENTORY' | 'PROCESS_ONLY';
  createdAt: string;
  updatedAt: string;
}

export interface JobWork {
  id: number;
  jobWorkCode: string;
  vendorId: number;
  jobTypeId: number;
  designNo: string;
  designCompanyId: number;
  issueDate: string;
  expectedReturn?: string;
  actualReturn?: string;
  status: JobWorkStatus;
  type: IJobWorkType;
  isSampleGiven: boolean;
  isSampleReceived?: boolean;
  processMaterial: boolean;
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceAmount?: number;
  taxAmount?: number;
  totalAmount?: number;
  invoiceImageUrl?: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  companyId: number;
  materials: JobWorkMaterial[];
  designDetails: JobWorkDesignDetail[];
  receivedItems: JobWorkReceivedItem[];
  vendor?: {
    id: number;
    registered_name: string;
    alias?: string;
    phone?: string;
    address?: string;
    is_internal?: boolean;
  };
}

export interface JobWorkMaterial {
  id: number;
  jobWorkId: number;
  materialId: number;
  quantity: number;
  uom: string;
  rate?: number;
  comments?: string;
  createdAt: string;
  updatedAt: string;
  companyId: number;
}

export interface JobWorkDesignDetail {
  id: number;
  jobWorkId: number;
  garmentPart: string;
  size: string;
  pieces: number;
  rate?: number;
  comments?: string;
  createdAt: string;
  updatedAt: string;
  companyId: number;
}

export interface JobWorkReceivedItem {
  id: number;
  jobWorkId: number;
  designNo: string;
  garmentPart: string;
  size: string;
  materialId?: number;
  materialQuantity?: number;
  materialUom?: string;
  pieceCount: number;
  defectQuantity?: number;
  shortageQuantity?: number;
  wastageQuantity?: number;
  processingRate?: number;
  remarks?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  companyId: number;
}

export enum JobWorkStatus {
  PENDING = 'PENDING',
  ISSUED = 'ISSUED',
  PROCESSING = 'PROCESSING',
  PARTIALLY_RECEIVED = 'PARTIALLY_RECEIVED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum IssueType {
  REGULAR = 'REGULAR',
  SAMPLE = 'SAMPLE'
}

export enum ReceiptType {
  REGULAR = 'REGULAR',
  SAMPLE = 'SAMPLE'
}

export interface IssuedItem {
  id: number;
  jobWorkId: number;
  designNo: string;
  garmentPart: string;
  size: string;
  quantity: number;
  issueDate: string;
  issueType: IssueType;
  createdAt: string;
  updatedAt: string;
  companyId: number;
}

export interface ReceivedItem {
  id: number;
  jobWorkId: number;
  designNo: string;
  garmentPart: string;
  size: string;
  materialQuantity?: number;
  materialUom?: string;
  pieceCount: number;
  defectQuantity?: number;
  shortageQuantity?: number;
  wastageQuantity?: number;
  processingRate?: number;
  remarks?: string;
  createdAt: string;
  updatedAt: string;
  companyId: number;
}

export interface CreateJobWorkDTO {
  vendorId: number;
  jobTypeId: number;
  designNo: string;
  issueDate: string;
  expectedReturn?: string;
  isSampleGiven: boolean;
  processMaterial: boolean;
  garment_part?: string | null;
  notes?: string;
  materials: {
    materialId: number;
    quantity: number;
    uom: string;
    rate?: number;
    comments?: string;
  }[];
  designDetails: {
    garmentPart: string;
    size: string;
    pieces: number;
    rate: number;
    comments?: string;
  }[];
}

export interface ReceiveJobWorkDTO {
  receiveDate: string;
  isSampleReceived: boolean;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: number;
  taxAmount?: number;
  invoiceImageUrl?: string;
  receivedItems: {
    designNo: string;
    garmentPart: string;
    size: string;
    materialId?: number;
    materialQuantity?: number;
    materialUom?: string;
    pieceCount?: number;
    defectQuantity?: number;
    shortageQuantity?: number;
    wastageQuantity?: number;
    processingRate?: number;
    remarks?: string;
  }[];
  isComplete?: boolean;
}

export interface IssueJobWorkDTO {
  issue_date: string;
  notes?: string;
}

const jobWorksApi = {
  getAll: async () => {
    const response = await instance.get('/job-works');
    return response.data;
  },

  getById: async (id: number) => {
    const response = await instance.get(`/job-works/${id}`);
    return response.data;
  },

  getJobWorkById: async (id: number) => {
    console.log(`Making request to /job-works/${id}`);
    console.log('Token:', localStorage.getItem('token'));
    console.log('Base URL:', instance.defaults.baseURL);
    
    try {
      const response = await instance.get(`/job-works/${id}`);
      console.log('Response received:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      return response;
    } catch (error: any) {
      console.error('Error in getJobWorkById:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      throw error;
    }
  },

  create: async (data: CreateJobWorkDTO) => {
    const response = await instance.post('/job-works', data);
    return response.data;
  },

  update: async (id: number, data: Partial<CreateJobWorkDTO>) => {
    const response = await instance.patch(`/job-works/${id}`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await instance.delete(`/job-works/${id}`);
    return response.data;
  },

  getJobTypes: async () => {
    const response = await instance.get('/job-work-types');
    return response.data;
  },

  receive: async (id: number, data: ReceiveJobWorkDTO) => {
    const response = await instance.post(`/job-works/${id}/receive`, data);
    return response.data;
  },

  uploadInvoiceImage: async (formData: FormData) => {
    const response = await instance.post('/upload', formData, {
      headers: {
        'Content-Type': undefined
      }
    });
    return response.data;
  },

  markAsComplete: async (id: number) => {
    const response = await instance.post(`/job-works/${id}/complete`);
    return response.data;
  },

  markAsProcessing: async (id: number) => {
    const response = await instance.post(`/job-works/${id}/process`);
    return response.data;
  },

  issue: async (id: number, data: IssueJobWorkDTO) => {
    const response = await instance.post(`/job-works/${id}/issue`, data);
    return response.data;
  },

  addInvoice: async (id: number, data: { invoiceNumber: string; invoiceDate?: string; invoiceAmount: number; taxAmount?: number; invoiceImageUrl: string }) => {
    const response = await instance.post(`/job-works/${id}/invoice`, data);
    return response.data;
  },

  cancel: async (id: number) => {
    const response = await instance.post(`/job-works/${id}/cancel`);
    return response.data;
  },

  bulkCancel: async (ids: number[]) => {
    const response = await instance.post('/job-works/bulk-cancel', { ids });
    return response.data;
  },
  
  updateReceipt: async (jobWorkId: number, receiptId: number, data: Partial<ReceiveJobWorkDTO>) => {
    if (!jobWorkId || !receiptId) {
      throw new Error('Missing required parameters: jobWorkId and receiptId are required');
    }
    console.log(`Calling API: PUT /job-works/${jobWorkId}/edit-receipt/${receiptId}`);
    console.log('With data:', data);
    const response = await instance.put(`/job-works/${jobWorkId}/edit-receipt/${receiptId}`, data);
    return response.data;
  },

  deleteReceipt: async (jobWorkId: number, receiptId: number) => {
    try {
      if (!jobWorkId || isNaN(jobWorkId) || jobWorkId <= 0) {
        console.error(`Invalid jobWorkId: ${jobWorkId} (${typeof jobWorkId})`);
        throw new Error(`Invalid jobWorkId: ${jobWorkId}`);
      }
      
      if (!receiptId || isNaN(receiptId) || receiptId <= 0) {
        console.error(`Invalid receiptId: ${receiptId} (${typeof receiptId})`);
        throw new Error(`Invalid receiptId: ${receiptId}`);
      }
      
      console.log(`Calling API: DELETE /job-works/${jobWorkId}/receipts/${receiptId}`);
      const response = await instance.delete(`/job-works/${jobWorkId}/receipts/${receiptId}`);
      return response.data;
    } catch (error) {
      console.error('Error in deleteReceipt:', error);
      throw error; // Re-throw the error for the caller to handle
    }
  },

  createReceipt: async (jobWorkId: number, data: Partial<ReceiveJobWorkDTO>) => {
    if (!jobWorkId) {
      throw new Error('Missing required parameter: jobWorkId is required');
    }
    const response = await instance.post(`/job-works/${jobWorkId}/receive`, data);
    return response.data;
  }
};

export default jobWorksApi; 